import {
  collection,
  doc,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';

import { firestore, auth } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { Usuario, Rol } from '@/types/domain';

const userConverter: FirestoreDataConverter<Usuario> = {
  toFirestore(d: WithFieldValue<Usuario>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<Usuario, 'uid'>;
    return { uid: snap.id, ...data };
  },
};

export const usersCollection = collection(firestore, 'users').withConverter(userConverter);

export const usuariosQuery: any = USE_API
  ? apiQuery('/usuarios', { _order: 'nombre' })
  : query(usersCollection, orderBy('nombre'));

export interface CrearUsuarioInput {
  email: string;
  uid?: string;
  password?: string; // opcional: si no se pasa, la fija la persona al primer ingreso
  nombre: string;
  rol: Rol;
  telefono?: string;
}

/**
 * Crea un nuevo usuario. Si no se pasa password, la cuenta arranca sin contraseña
 * y la persona la fija en su primer ingreso.
 */
export async function crearUsuario(input: CrearUsuarioInput) {
  if (USE_API) {
    const res = await api.post('/usuarios', {
      email: input.email.trim(),
      uid: input.uid ?? input.email.trim(),
      nombre: input.nombre.trim(),
      rol: input.rol,
      password: input.password,
      telefono: input.telefono ?? null,
      activo: true,
    });
    return res.uid;
  }
  const cred = await createUserWithEmailAndPassword(auth, input.email.trim(), input.password ?? Math.random().toString(36).slice(2));
  const uid = cred.user.uid;
  await setDoc(doc(usersCollection, uid), {
    email: input.email.trim(),
    nombre: input.nombre.trim(),
    rol: input.rol,
    telefono: input.telefono ?? null,
    activo: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as any);
  return uid;
}

/** Deja la cuenta sin contraseña: la persona crea una nueva en su próximo ingreso. */
export async function restablecerPassword(uid: string) {
  if (USE_API) {
    await api.patch(`/usuarios/${uid}`, { resetPassword: true });
    return;
  }
  // En modo Firebase el reset de password se hace por email; no aplica acá.
  throw new Error('Restablecer contraseña solo disponible en el modo servidor.');
}

export async function actualizarUsuario(uid: string, patch: Partial<Pick<Usuario, 'nombre' | 'rol' | 'activo' | 'telefono'>>) {
  if (USE_API) {
    await api.patch(`/usuarios/${uid}`, patch);
    return;
  }
  await updateDoc(doc(usersCollection, uid), {
    ...patch,
    updatedAt: serverTimestamp(),
  } as any);
}
